import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  MapContainer,
  GeoJSON,
  CircleMarker,
  Tooltip,
  Popup,
  useMap,
} from 'react-leaflet';
import L from 'leaflet';
import type { ParcelDto, PhotoRecordDto } from '@forest/contracts';
import { SafeMap } from '@/components/ui/SafeMap';
import { Dialog } from '@/components/ui/Dialog';
import { ImageLightbox } from '@/components/ui/ImageLightbox';
import { MapLayerControl } from '@/components/map/MapLayerControl';
import { IcasLoadingIndicator } from '@/features/icas';
import { useParcelPhotos } from '@/features/photos/usePhotos';
import { photoHref } from '@/features/photos/photoLink';
import { PolygonStatusBadge } from '@/features/photos/PolygonStatusBadge';
import { driveThumb } from '@/lib/driveUrl';
import { SubParcelList } from '@/features/subparcels/SubParcelList';
import './PhotoMarkers.css';
import markerIconUrl from 'leaflet/dist/images/marker-icon.png?url';
import markerIcon2xUrl from 'leaflet/dist/images/marker-icon-2x.png?url';
import markerShadowUrl from 'leaflet/dist/images/marker-shadow.png?url';

L.Icon.Default.mergeOptions({
  iconUrl: markerIconUrl,
  iconRetinaUrl: markerIcon2xUrl,
  shadowUrl: markerShadowUrl,
});

export type ParcelViewMode = 'map' | 'image';

function toGeoJsonGeometry(
  geometryType: string | null,
  coords: unknown,
): GeoJSON.Geometry | null {
  if (!coords || !geometryType) return null;
  if (typeof coords === 'object' && !Array.isArray(coords)) {
    const obj = coords as Record<string, unknown>;
    if (typeof obj.type === 'string' && 'coordinates' in obj) {
      return obj as unknown as GeoJSON.Geometry;
    }
  }
  return {
    type: geometryType as GeoJSON.Geometry['type'],
    coordinates: coords as unknown as number[],
  } as GeoJSON.Geometry;
}

/**
 * Flatten any nested coordinate array into a list of [lon, lat] tuples
 * so we can compute the bbox and render a self-contained SVG silhouette.
 */
function flattenCoords(coords: unknown): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  const walk = (v: unknown) => {
    if (!Array.isArray(v)) return;
    // A leaf coordinate is [lon, lat] — two numbers.
    if (typeof v[0] === 'number' && typeof v[1] === 'number') {
      out.push([v[0] as number, v[1] as number]);
      return;
    }
    for (const c of v) walk(c);
  };
  walk(coords);
  return out;
}

function computeBbox(
  coords: Array<[number, number]>,
): { minLon: number; minLat: number; maxLon: number; maxLat: number } | null {
  if (coords.length === 0) return null;
  let minLon = Infinity;
  let minLat = Infinity;
  let maxLon = -Infinity;
  let maxLat = -Infinity;
  for (const [lon, lat] of coords) {
    if (lon < minLon) minLon = lon;
    if (lat < minLat) minLat = lat;
    if (lon > maxLon) maxLon = lon;
    if (lat > maxLat) maxLat = lat;
  }
  return { minLon, minLat, maxLon, maxLat };
}

/** Auto-fit the map to the provided GeoJSON feature on first load. */
function FitBounds({ feature }: { feature: GeoJSON.Feature }) {
  const map = useMap();
  const fitted = useRef(false);
  useEffect(() => {
    if (fitted.current) return;
    try {
      const layer = L.geoJSON(feature);
      const bounds = layer.getBounds();
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [20, 20] });
        fitted.current = true;
      }
    } catch {
      // ignore
    }
  }, [feature, map]);
  return null;
}

type Props = {
  open: boolean;
  mode: ParcelViewMode;
  parcel: ParcelDto | null;
  onClose: () => void;
};

export function ParcelViewModal({ open, mode, parcel, onClose }: Props) {
  const { t } = useTranslation(['database']);
  const feature = useMemo<GeoJSON.Feature | null>(() => {
    if (!parcel) return null;
    const geom = toGeoJsonGeometry(parcel.geometryType, parcel.geometry);
    if (!geom) return null;
    return { type: 'Feature', properties: {}, geometry: geom };
  }, [parcel]);

  if (!parcel) return null;

  const titleKey = mode === 'map' ? 'database:modal.title.map' : 'database:modal.title.image';
  const title = t(titleKey, { raion: parcel.raion, primarie: parcel.primarie });

  return (
    <Dialog open={open} onClose={onClose} title={title} size="xl">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3 text-xs text-slate-600 sm:grid-cols-4">
          <Info label={t('database:modal.info.codProiect')} value={parcel.codProiect ?? '—'} mono />
          <Info label={t('database:modal.info.ucf')} value={parcel.ucf} mono />
          <Info label={t('database:modal.info.ocolSilvic')} value={parcel.ocolSilvic ?? '—'} />
          <Info label={t('database:modal.info.entitateSilvica')} value={parcel.entitateSilvica ?? '—'} />
          <Info label={t('database:modal.info.suprafata')} value={parcel.suprafata.toFixed(2)} />
          <Info label={t('database:modal.info.anul')} value={parcel.anul} />
          <Info label={t('database:modal.info.geometryType')} value={parcel.geometryType ?? '—'} />
          <Info
            label={t('database:modal.info.id')}
            value={parcel.sequenceId !== null ? String(parcel.sequenceId) : '—'}
          />
        </div>

        {mode === 'map' ? (
          <MapView feature={feature} parcelId={parcel.id} />
        ) : (
          <ImageView parcel={parcel} />
        )}

        <SubParcelList parcelId={parcel.id} />
      </div>
    </Dialog>
  );
}

function Info({
  label,
  value,
  mono,
}: {
  label: string;
  value: string | number;
  mono?: boolean;
}) {
  return (
    <div className="rounded border border-slate-200 bg-slate-50 px-2 py-1.5">
      <div className="text-[10px] uppercase text-slate-500">{label}</div>
      <div className={mono ? 'font-mono text-xs' : 'text-sm'}>{value}</div>
    </div>
  );
}

/**
 * The polygon's photos, drawn on top of it.
 *
 * Colour follows the polygon-status convention used everywhere else, so
 * a photo the server attached by proximity rather than containment is
 * visibly different from one that sits inside.
 *
 * Clicking a marker opens the photo's details in a popup on the map
 * itself. Leaving the polygon to read one photo loses the map position,
 * the zoom and the surrounding photos, which is the whole context the
 * user opened the polygon for. The popup carries a link into Baza de
 * date for anyone who does want the full record.
 */
function PhotoMarkers({
  parcelId,
  onOpenImage,
}: {
  parcelId: string;
  onOpenImage: (url: string, alt: string) => void;
}) {
  const { t } = useTranslation(['photos', 'database']);
  const { data } = useParcelPhotos(parcelId);
  const photos = data ?? [];
  if (photos.length === 0) return null;
  return (
    <>
      {photos.map((p) => (
        <CircleMarker
          key={p.id}
          center={[p.latitude, p.longitude]}
          radius={6}
          pathOptions={{
            color: '#ffffff',
            weight: 2,
            fillColor: statusColor(p.polygonStatus),
            fillOpacity: 0.95,
          }}
        >
          {/* Short on purpose: the details live in the popup, and a
              tooltip wide enough for a file name covers its neighbours. */}
          <Tooltip direction="top" offset={[0, -8]} className="photo-marker-label">
            <span className="photo-marker-name">
              <span className="font-mono">#{p.nr}</span> {p.fileName}
            </span>
          </Tooltip>
          <Popup className="photo-marker-popup" minWidth={240} maxWidth={240}>
            <div className="space-y-1.5">
              {p.downloadUrl ? (
                <button
                  type="button"
                  className="block w-full"
                  onClick={() =>
                    onOpenImage(driveThumb(p.downloadUrl, 1600), p.fileName)
                  }
                  title={t('photos:action.viewPhoto')}
                >
                  <img
                    src={driveThumb(p.downloadUrl, 400)}
                    alt={p.fileName}
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    className="h-32 w-full cursor-zoom-in rounded bg-slate-100 object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </button>
              ) : null}
              <div className="text-xs font-semibold break-words">
                <span className="font-mono text-slate-500">#{p.nr}</span> {p.fileName}
              </div>
              <div className="flex items-center gap-1.5">
                <PolygonStatusBadge status={p.polygonStatus} />
              </div>
              <dl className="grid grid-cols-[auto,1fr] gap-x-2 gap-y-0.5 text-[11px] text-slate-600">
                <dt>{t('photos:table.header.latitude')}</dt>
                <dd className="tabular-nums">{p.latitude.toFixed(6)}</dd>
                <dt>{t('photos:table.header.longitude')}</dt>
                <dd className="tabular-nums">{p.longitude.toFixed(6)}</dd>
                {p.author ? (
                  <>
                    <dt>{t('photos:table.header.author')}</dt>
                    <dd>{p.author.cod ?? p.author.fullName}</dd>
                  </>
                ) : null}
                {p.year ? (
                  <>
                    <dt>{t('photos:preview.anul')}</dt>
                    <dd className="tabular-nums">
                      {p.month ? `${String(p.month).padStart(2, '0')}.` : ''}
                      {p.year}
                    </dd>
                  </>
                ) : null}
                {p.speciaDeBaza ? (
                  <>
                    <dt>{t('photos:table.header.speciaDeBaza')}</dt>
                    <dd>{p.speciaDeBaza}</dd>
                  </>
                ) : null}
                {p.anulCultura ? (
                  <>
                    <dt>{t('photos:table.header.anulCultura')}</dt>
                    <dd className="tabular-nums">{p.anulCultura}</dd>
                  </>
                ) : null}
                {p.ultimaLucrare ? (
                  <>
                    <dt>{t('photos:table.header.ultimaLucrare')}</dt>
                    <dd className="break-words">{p.ultimaLucrare}</dd>
                  </>
                ) : null}
              </dl>
              <Link
                to={photoHref(p.id)}
                className="inline-block text-xs font-medium text-forest-700 hover:underline"
              >
                {t('photos:map.openInDatabase', {
                  defaultValue: 'Deschide în Baza de date',
                })}
              </Link>
            </div>
          </Popup>
        </CircleMarker>
      ))}
    </>
  );
}

/** Same palette as `PolygonStatusBadge`, so the map agrees with the table. */
function statusColor(status: PhotoRecordDto['polygonStatus']): string {
  switch (status) {
    case 'IN_POLYGON':
      return '#16a34a';
    case 'AUTO_ADJUSTED':
      return '#eab308';
    case 'MANUAL_ADJUSTED':
      return '#f97316';
    default:
      return '#dc2626';
  }
}

function MapView({
  feature,
  parcelId,
}: {
  feature: GeoJSON.Feature | null;
  parcelId: string;
}) {
  const { t } = useTranslation(['database']);
  const [lightbox, setLightbox] = useState<{ url: string; alt: string } | null>(
    null,
  );
  const openImage = useCallback(
    (url: string, alt: string) => setLightbox({ url, alt }),
    [],
  );
  if (!feature) {
    return (
      <div className="rounded border border-slate-200 bg-slate-50 p-8 text-center text-sm text-slate-500">
        {t('database:map.noGeometry')}
      </div>
    );
  }
  return (
    <div className="relative">
      <SafeMap height={480} className="overflow-hidden rounded border border-slate-200">
        <MapContainer
          center={[47.0105, 28.8638]}
          zoom={8}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom
        >
          <MapLayerControl />
          <GeoJSON
            data={feature}
            style={() => ({
              color: '#15803d',
              weight: 2,
              fillColor: '#22c55e',
              fillOpacity: 0.4,
            })}
          />
          <PhotoMarkers parcelId={parcelId} onOpenImage={openImage} />
          <FitBounds feature={feature} />
        </MapContainer>
      </SafeMap>
      <IcasLoadingIndicator />
      <ImageLightbox
        url={lightbox?.url ?? null}
        alt={lightbox?.alt}
        caption={lightbox?.alt}
        onClose={() => setLightbox(null)}
      />
    </div>
  );
}

/**
 * Image view = a self-contained SVG silhouette of the polygon. We don't have
 * raster attachments yet, so we render the geometry as a black-on-white
 * vector preview — useful as a thumbnail-style visualization without any
 * external assets or network calls.
 */
function ImageView({ parcel }: { parcel: ParcelDto }) {
  const { t } = useTranslation(['database']);
  const svg = useMemo(() => buildSvg(parcel), [parcel]);
  if (!svg) {
    return (
      <div className="rounded border border-slate-200 bg-slate-50 p-8 text-center text-sm text-slate-500">
        {t('database:map.noImage')}
      </div>
    );
  }
  return (
    <div className="flex justify-center rounded border border-slate-200 bg-white p-4">
      <div
        className="max-h-[460px] w-full"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: svg }}
      />
    </div>
  );
}

function buildSvg(parcel: ParcelDto): string | null {
  const points = flattenCoords(parcel.geometry);
  if (points.length < 3) return null;
  const bbox = computeBbox(points);
  if (!bbox) return null;
  const width = 800;
  const height = 500;
  const padding = 16;
  const lonRange = bbox.maxLon - bbox.minLon || 1e-6;
  const latRange = bbox.maxLat - bbox.minLat || 1e-6;
  const scale = Math.min(
    (width - padding * 2) / lonRange,
    (height - padding * 2) / latRange,
  );
  const project = (lon: number, lat: number): [number, number] => [
    padding + (lon - bbox.minLon) * scale,
    // flip Y: SVG grows downward, latitude grows upward
    height - padding - (lat - bbox.minLat) * scale,
  ];

  // Re-walk the raw coordinates so we preserve polygon ring structure.
  const rings: Array<Array<[number, number]>> = [];
  const walkRings = (v: unknown) => {
    if (!Array.isArray(v)) return;
    if (
      v.length > 0 &&
      Array.isArray(v[0]) &&
      typeof (v[0] as unknown[])[0] === 'number'
    ) {
      // This level is an array of [lon, lat] points — it's a ring.
      const ring: Array<[number, number]> = [];
      for (const p of v as unknown[]) {
        if (Array.isArray(p) && typeof p[0] === 'number' && typeof p[1] === 'number') {
          ring.push(project(p[0] as number, p[1] as number));
        }
      }
      if (ring.length > 2) rings.push(ring);
      return;
    }
    for (const c of v) walkRings(c);
  };
  walkRings(parcel.geometry);

  if (rings.length === 0) return null;

  const paths = rings
    .map(
      (ring) =>
        'M ' +
        ring.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' L ') +
        ' Z',
    )
    .join(' ');

  return `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="100%" preserveAspectRatio="xMidYMid meet">
      <rect width="100%" height="100%" fill="#f8fafc" />
      <path d="${paths}" fill="#22c55e" fill-opacity="0.55" stroke="#15803d" stroke-width="1.5" />
    </svg>
  `;
}
